/**
 * Utility functions for masking phone numbers based on handler permissions.
 */

/**
 * Checks if the given user object has permission to view full (unmasked) phone numbers.
 * Super Admin always has full view access.
 * Handlers have full view access only if mobile_number_full permission power is true.
 *
 * @param {Object} user - Current logged-in user object
 * @returns {boolean}
 */
export function canViewFullPhone(user) {
  if (!user) return false;
  if (user.role === 'super_admin' || user.role === 'admin') return true;
  
  return Boolean(
    user.settings_powers?.mobile_number_full ||
    user.permissions?.mobile_number_full ||
    user.mobile_number_full
  );
}

/**
 * Masks a phone number string if hasFullAccess is false.
 * Shows first 2 digits and last 2 digits, masking the middle digits with asterisks.
 * e.g. "9876543210" -> "98******10"
 * e.g. "+919876543210" -> "+91 98******10"
 *
 * @param {string} phone - The phone number string to mask
 * @param {boolean} hasFullAccess - Whether full view is allowed
 * @returns {string}
 */
export function maskPhone(phone, hasFullAccess = false) {
  if (hasFullAccess || !phone) return phone;
  
  const str = String(phone).trim();
  if (!str || str.startsWith('GUEST_')) return str;

  // Extract raw digits
  const digits = str.replace(/\D/g, '');
  if (digits.length < 4) return str;

  if (digits.length <= 6) {
    const masked = digits[0] + '*'.repeat(digits.length - 2) + digits[digits.length - 1];
    return str.startsWith('+') ? `+${masked}` : masked;
  }

  // Handle +91 prefix case or 10-12 digit numbers
  if (digits.length >= 10 && digits.startsWith('91')) {
    const localDigits = digits.slice(2);
    const maskedLocal = localDigits.slice(0, 2) + '*'.repeat(localDigits.length - 4) + localDigits.slice(-2);
    return str.startsWith('+') ? `+91 ${maskedLocal}` : `91 ${maskedLocal}`;
  }

  const firstTwo = digits.slice(0, 2);
  const lastTwo = digits.slice(-2);
  const maskedMiddle = '*'.repeat(digits.length - 4);
  const maskedDigits = `${firstTwo}${maskedMiddle}${lastTwo}`;

  return str.startsWith('+') ? `+${maskedDigits}` : maskedDigits;
}
